import { AppURLs } from './appURLs';
import axios from 'axios';
import { Modal } from 'antd';
import environment from 'app/config/environment';
import { getFirebaseAccessToken } from './firebase';
import logger from 'app/components/common/ErrorLogger';
import { applyActiveBrandHeader } from 'app/utils/localStorageHandler/brandData';

const api = axios.create({
  baseURL: environment.api.API_BASE_URL,
});

const pendingRequests = new Map();

// Keyed on the full URL: stripping the query string made `?includeDeleted=true` collide with the plain
// request, so the two cancelled each other.
const getRequestKey = (config: any) => {
  const { url, method } = config;
  return `${method}:${url}`;
};

// De-duplication cancels the older of two identical in-flight GETs, so one of the two callers loses.
// That is fine when both are the same component re-fetching, and wrong when they are different
// components that each need an answer — the loser now gets a rejection rather than the hang it used to
// get, but a rejection it did not ask for is still wrong. Endpoints that several independent
// components fetch concurrently opt out entirely.
const DEDUPE_EXEMPT = [/^\/transaction\/[^/?]+$/];

const isDedupeExempt = (url: string) => {
  const path = url.split('?')[0];
  return DEDUPE_EXEMPT.some((pattern) => pattern.test(path));
};

api.interceptors.request.use(
  async (config: any) => {
    const token = await getFirebaseAccessToken();
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token;
    }

    applyActiveBrandHeader(config);

    if (
      config?.method?.toLowerCase() === 'get' &&
      !config?.url?.includes('/user/profile') &&
      !config?.url?.includes('/office') &&
      !isDedupeExempt(config?.url ?? '')
    ) {
      const requestKey = getRequestKey(config);

      // Cancel previous requests with the same signature
      const pending = pendingRequests.get(requestKey);
      if (pending) {
        console.log('Cancelling previous request:', requestKey);
        pending.cancel('Request superseded by newer request');
        pendingRequests.delete(requestKey);
      }

      // Create cancel token
      const cancelTokenSource = axios.CancelToken.source();
      config.cancelToken = cancelTokenSource.token;

      // Store the cancel function
      pendingRequests.set(requestKey, {
        cancel: cancelTokenSource.cancel,
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    const requestKey = getRequestKey(response.config);
    pendingRequests.delete(requestKey);
    return response;
  },
  (error) => {
    if (error.config) {
      const requestKey = getRequestKey(error.config);
      pendingRequests.delete(requestKey);
    }

    if (axios.isCancel(error)) {
      console.log('Request cancelled:', error.message);
      return new Promise(() => {});
    }

    const status = error.response?.status;
    const url = error.config?.url ?? 'unknown';
    const method = (error.config?.method ?? 'unknown').toUpperCase();
    logger.log({
      type: 'API_ERROR',
      level: status && status >= 500 ? 'error' : 'warn',
      message: `${method} ${url} → ${status ?? 'network error'}`,
      error: error instanceof Error ? error : undefined,
      tags: {
        endpoint: url,
        method,
        status_code: String(status ?? 'network'),
      },
      metadata: { responseData: error.response?.data },
    });

    if (error.response && error.response.status === 401) {
      localStorage.removeItem('authToken');
      if (error.config.url.includes('/compliance/')) {
        window.location.href = AppURLs.agentLoginSessionExpired();
      } else if (error.config.url.includes('/agent/')) {
        window.location.href = AppURLs.agentLoginSessionExpired();
      } else if (error.config.url.includes('/office-admin')) {
        window.location.href = AppURLs.agentLoginSessionExpired();
      }
    }

    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data?.isViewOnly
    ) {
      Modal.warning({
        title: 'View-Only Access',
        content:
          'Your account has view-only access. You can browse and explore the platform, but cannot make any changes.',
      });
    }

    return Promise.reject(error);
  }
);

export default api;
